import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { toast } from '@hivebrite/design-system/Toast';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { Textarea } from 'hb-react/shared/components/formik/components/inputs';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

const ReportContent = ({ id, type, onCloseModal }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousPendingReport, setPreviousPendingReport] = useState(null);

  const formikRef = useRef();

  const onError = useCallback(() => {
    toast.error(__('Sorry, an error occurred'));
    setIsSubmitting(false);
  }, []);
  const handleDelete = useCallback(() => {
    setIsSubmitting(true);
    axios
      .delete(Routes.content_reporting_path(previousPendingReport?.id))
      .then(() => {
        toast.success(I18n.t('web.content_reporting.deleted_message'));
        onCloseModal();
      })
      .catch(onError);
  }, [previousPendingReport, onCloseModal]);
  const handleSubmitClick = useCallback(
    () => formikRef.current?.submitForm(),
    [formikRef],
  );
  const handleSubmit = useCallback(
    (values) => {
      setIsSubmitting(true);

      if (previousPendingReport?.id) {
        axios
          .put(Routes.content_reporting_path(previousPendingReport.id), values)
          .then(() => {
            toast.success(I18n.t('web.content_reporting.updated_message'));
            onCloseModal();
          })
          .catch(onError);
      } else {
        axios
          .post(Routes.content_reportings_path(), values)
          .then(() => {
            toast.success(I18n.t('web.content_reporting.created_message'));
            onCloseModal();
          })
          .catch(onError);
      }
    },
    [previousPendingReport],
  );

  useEffect(() => {
    axios
      .get(
        Routes.pending_report_content_reportings_path({
          reportable_id: id,
          reportable_type: type,
        }),
      )
      .then((response) => {
        setPreviousPendingReport(response?.data?.reported_content);
        setIsLoading(false);
      });
  }, []);

  const footer = useMemo(
    () => (
      <Box mt={6} justifyContent="flex-end">
        {!previousPendingReport?.message && (
          <Button variant="secondary" onClick={onCloseModal}>
            {__('Cancel')}
          </Button>
        )}

        {previousPendingReport?.message && (
          <Box ml={4}>
            <Button
              variant="secondary"
              onClick={handleDelete}
              isDisabled={isSubmitting}
            >
              {I18n.t('actions.delete')}
            </Button>
          </Box>
        )}
        <Box ml={4}>
          <Button
            isDisabled={isLoading || isSubmitting}
            onClick={handleSubmitClick}
          >
            {previousPendingReport?.message ? __('Update') : __('Send')}
          </Button>
        </Box>
      </Box>
    ),
    [previousPendingReport, isSubmitting, isLoading],
  );

  return (
    <ModalWithTemplate
      onCloseModal={onCloseModal}
      header={I18n.t('web.content_reporting.modal_title')}
      isLoading={isLoading}
      screenReadersCloseButtonText={s__(
        'ReportContentAccessibility|Close the report content window',
      )}
    >
      <Formik
        initialValues={{
          reported_content: {
            message: previousPendingReport?.message,
            reportable_id: id,
            reportable_type: type,
          },
        }}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          formikRef.current = formik;

          return (
            <Box width="100%" flexDirection="column">
              <Form>
                {previousPendingReport?.message && (
                  <Box
                    mb={4}
                    dangerouslySetInnerHTML={{
                      __html: I18n.t('web.content_reporting.edit_message', {
                        creation_date: dayjs(
                          previousPendingReport?.updated_at,
                        ).format('LLL'),
                      }),
                    }}
                  />
                )}
                <Textarea
                  minHeight={90}
                  label={__('Report message')}
                  name="reported_content[message]"
                  placeholder={I18n.t(
                    'web.content_reporting.placeholder_message',
                  )}
                  validate={{ required: true }}
                />
                {footer}
              </Form>
            </Box>
          );
        }}
      </Formik>
    </ModalWithTemplate>
  );
};

ReportContent.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

export default ReportContent;
